import React from "react";
import {
  Paintbrush,
  Snowflake,
  Factory,
  Hammer,
  Wrench,
  Droplets,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SERVICE_IMAGES } from "@/lib/brand";
import { SERVICE_KEYS } from "@/i18n";
import { TID } from "@/constants/testIds/dv";

const ICONS = {
  painting: Paintbrush,
  insulation: Snowflake,
  commercial_insulation: Factory,
  drywall: Hammer,
  remodeling: Wrench,
  pressure_wash: Droplets,
  cleaning: Sparkles,
};

// Tetris grid spans (md and up). Mobile is 1 column.
const SPAN = {
  painting: "md:col-span-2 md:row-span-2",
  commercial_insulation: "md:col-span-2",
  insulation: "md:col-span-1",
  drywall: "md:col-span-1",
  remodeling: "md:col-span-2",
  pressure_wash: "md:col-span-1",
  cleaning: "md:col-span-1",
};

export const Services = () => {
  const { t } = useLanguage();

  return (
    <section
      id="services"
      data-testid={TID.servicesSection}
      className="relative bg-background py-24 md:py-32"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#E63946]" />
              <span className="label-eyebrow">{t("services.eyebrow")}</span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-foreground max-w-2xl">
              {t("services.title")}
            </h2>
          </div>
          <p className="md:max-w-md text-muted-foreground text-base leading-relaxed">
            {t("services.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:auto-rows-[14rem]">
          {SERVICE_KEYS.map((key) => {
            const Icon = ICONS[key] || Paintbrush;
            return (
              <a
                key={key}
                href="#contact"
                data-testid={TID.serviceCard(key)}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className={`group relative overflow-hidden rounded-sm border border-border bg-black ${
                  SPAN[key] || ""
                }`}
              >
                <img
                  src={SERVICE_IMAGES[key]}
                  alt={t(`services.items.${key}.name`)}
                  className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
                <div className="relative h-full p-6 flex flex-col justify-between min-h-[14rem]">
                  <div className="flex items-start justify-between">
                    <div className="h-10 w-10 grid place-items-center bg-[#E63946] text-white rounded-sm">
                      <Icon className="h-5 w-5" />
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-white/60 group-hover:text-white group-hover:rotate-45 transition-all" />
                  </div>
                  <div>
                    <h3 className="font-display uppercase text-xl md:text-2xl font-bold text-white tracking-tight">
                      {t(`services.items.${key}.name`)}
                    </h3>
                    <p className="mt-2 text-white/70 text-sm leading-relaxed max-w-md">
                      {t(`services.items.${key}.desc`)}
                    </p>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
